import React from "react";
import useTokenPrices from "../../hooks/useTokenPrices";

const BudgetTable = ({
  budget_allocation,
  currencyToken,
}) => {
  const { toDollar } = useTokenPrices();

  // Calculate total budget
  const totalBudget = budget_allocation?.reduce((acc, item) => {
    return acc + item.subCategory.reduce((subAcc, sub) => subAcc + Number(sub.budget || 0), 0);
  }, 0);

  return (
    <div className="w-full space-y-6">
      {budget_allocation?.map((item, categoryIndex) => {
        const { subCategory, category } = item ?? {};
        const categoryTotal = subCategory.reduce((acc, sub) => acc + Number(sub.budget || 0), 0);
        
        return (
          <div key={categoryIndex} className="rounded-2xl overflow-hidden shadow-md bg-white border border-gray-100">
            {/* Category Header */}
            <div className="bg-gradient-to-r from-primary to-[#7e7cff] px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-white">{category}</h3>
                </div>
                <div className="flex flex-col items-end">
                  <span className="text-white/70 text-xs uppercase tracking-wide">Category Total</span>
                  <span className="text-2xl font-bold text-white">${categoryTotal.toLocaleString()}</span>
                </div>
              </div>
            </div>
            
            {/* Subcategories */}
            <div className="divide-y divide-gray-100">
              {subCategory.map((sub, subIndex) => (
                <div
                  key={`${categoryIndex}-${subIndex}`}
                  className="px-6 py-4 hover:bg-gray-50/50 transition-colors group"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary group-hover:scale-150 transition-transform"></div>
                        <h4 className="text-base font-semibold text-secondary group-hover:text-primary transition-colors">
                          {sub?.name}
                        </h4>
                      </div>
                      {sub?.detail && (
                        <p className="text-sm text-gray-600 ml-3.5 leading-relaxed">
                          {sub?.detail}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3 md:ml-6">
                      <div className="flex flex-col items-end bg-gradient-to-br from-gray-50 to-white px-4 py-2 rounded-xl border border-gray-100 min-w-[140px]">
                        <span className="text-xs text-gray-500 uppercase tracking-wide">Amount</span>
                        <span className="text-xl font-bold text-primary mt-0.5">
                          ${Number(sub?.budget || 0).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      
      {/* Total Summary Card */}
      <div className="rounded-2xl overflow-hidden shadow-lg bg-gradient-to-r from-secondary to-[#1a1230]">
        <div className="px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <p className="text-white/70 text-sm uppercase tracking-wide">Total Project Budget</p>
              <p className="text-white/50 text-xs mt-0.5">Sum of all categories</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-white">${totalBudget?.toLocaleString()}</p>
            <p className="text-white/60 text-sm mt-1">{budget_allocation?.length} {budget_allocation?.length === 1 ? 'category' : 'categories'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetTable;
